import { Box, Select, Stack, Tooltip, Typography } from '@dtx-company/design-system/src'
import {
  CodeConfigurationField,
  LandingPageDestination
} from '@dtx-company/inter-app/src/types/flowcode'
import { EmailField } from './EmailField/EmailField'
import { FC, useState } from 'react'
import { FacebookField } from './FacebookField/FacebookField'
import { FileField } from './FileField/FileField'
import { GoogleDocField } from './GoogleDocField/GoogleDocField'
import { InstagramField } from './InstagramField/InstagramField'
import { SMSField } from './SMSField/SMSField'
import { ScanDestinationRadioCard } from './ScanDestinationRadioCard'
import { URLField } from './URLField/URLField'
import { YouTubeField } from './YouTubeField/YouTubeField'
import { fireAnalyticsEvent } from '@dtx-company/inter-app/src/event-tracking/helpers/fireAnalyticsEvent'
import { selectScanDestinationType } from '@dtx-company/app/shared/code/src/machines/locg/locgSelectors'
import { useGlobalServices } from '@dtx-company/app/shared/common/src/hooks/useGlobalServices'
import { useScanDestinationOptions } from './hooks/useScanDestinationOptions'
import { useScreenSizeBreakpoints } from '@dtx-company/app/shared/common/src/hooks/useScreenSizeBreakpoints'
import { useSelector } from '@xstate/react'
import HelpOutlineIcon from '@mui/icons-material/HelpOutline'
import useTranslation from 'next-translate/useTranslation'

export interface ScanDestinationFieldProps {
  size?: 'small' | 'medium' | 'large'
}

export const ScanDestination: FC<{ hideTitle?: boolean }> = ({ hideTitle }) => {
  const { t } = useTranslation('logged-out-generator')
  const scanDestinationOptions = useScanDestinationOptions()

  const { locgService } = useGlobalServices()
  const selectedScanDestinationType = useSelector(locgService, selectScanDestinationType)
  const [isOpen, setIsOpen] = useState(false)
  const { isMobile } = useScreenSizeBreakpoints()

  const ScanDestinationField = {
    [LandingPageDestination.URL]: URLField,
    [LandingPageDestination.FILE]: FileField,
    [LandingPageDestination.SMS]: SMSField,
    [LandingPageDestination.EMAIL]: EmailField,
    [LandingPageDestination.INSTAGRAM]: InstagramField,
    [LandingPageDestination.FACEBOOK]: FacebookField,
    [LandingPageDestination.YOUTUBE]: YouTubeField,
    [LandingPageDestination.GOOGLE_DOC]: GoogleDocField
  }[selectedScanDestinationType]

  return (
    <>
      <Stack
        gap={4}
        sx={theme => ({
          // give space for the focus indicator on the ScanDestinationField
          pb: 1,
          width: '100%',
          // Don't show input adornments on mobile
          [`${theme.breakpoints.down('md')}`]: {
            '.MuiInputAdornment-root': {
              display: 'none'
            }
          }
        })}
      >
        {/* Mobile view */}
        <Stack
          gap={4}
          sx={{
            display: {
              md: 'none'
            }
          }}
        >
          <Stack>
            <Stack direction={'row'} gap={2} alignItems={'center'}>
              <Typography component="label" variant="labelLarge">
                {t('LoggedOutGenerator.ScanDestination.heading')}
              </Typography>
              <Tooltip
                arrow
                open={isOpen && isMobile}
                onClose={() => {
                  fireAnalyticsEvent('LOCG_ScanDestination_tooltip_closed')
                  setIsOpen(false)
                }}
                onOpen={() => {
                  fireAnalyticsEvent('LOCG_ScanDestination_tooltip_open')
                  setIsOpen(true)
                }}
                title={t('LoggedOutGenerator.ScanDestination.tooltipContent')}
              >
                <HelpOutlineIcon
                  fontSize={'inherit'}
                  aria-hidden="false"
                  role="button"
                  tabIndex={0}
                  aria-label="scan destination tooltip"
                />
              </Tooltip>
            </Stack>

            <Typography component="p" variant="captionMedium" color="text.secondary">
              {t('LoggedOutGenerator.ScanDestination.description')}
            </Typography>
          </Stack>
          <Select
            size="large"
            label={t('LoggedOutGenerator.ScanDestination.heading')}
            hiddenLabel
            name={CodeConfigurationField.SCAN_DESTINATION_TYPE}
            value={selectedScanDestinationType}
            onChange={event => {
              locgService.send({
                type: 'CHANGE',
                key: CodeConfigurationField.SCAN_DESTINATION_TYPE,
                value: event.target.value as LandingPageDestination
              })
            }}
            options={Object.values(scanDestinationOptions).map(({ label, value, Icon }) => ({
              label: (
                <Stack
                  direction="row"
                  alignItems="center"
                  gap={3}
                  sx={{ color: 'onSurface.secondary' }}
                >
                  <Icon />
                  {label}
                </Stack>
              ),
              value
            }))}
          />
        </Stack>

        {/* Desktop view */}
        <Box
          component="fieldset"
          sx={{
            display: {
              xs: 'none',
              md: 'block'
            },
            border: 'none',
            p: 0,
            m: 0,
            legend: {
              p: 0
            }
          }}
        >
          {!hideTitle && <ScanDestinationTitle />}
          <ScanDestinationFieldsOptions />
        </Box>

        <ScanDestinationField />
      </Stack>
    </>
  )
}

export const ScanDestinationTitle: FC = () => {
  const { t } = useTranslation('logged-out-generator')

  return (
    <>
      <legend style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
        <Typography variant="headlineXSmall">
          {t('LoggedOutGenerator.ScanDestination.heading')}
        </Typography>
        <Tooltip
          arrow
          placement={'right'}
          onClose={() => {
            fireAnalyticsEvent('LOCG_ScanDestination_tooltip_closed')
          }}
          onOpen={() => {
            fireAnalyticsEvent('LOCG_ScanDestination_tooltip_open')
          }}
          title={t('LoggedOutGenerator.ScanDestination.tooltipContent')}
        >
          <HelpOutlineIcon
            fontSize={'small'}
            role="button"
            tabIndex={0}
            aria-hidden="false"
            aria-label="scan destination tooltip"
          />
        </Tooltip>
      </legend>
      <Box
        sx={{
          mt: 0.5
        }}
      >
        <Typography component="p" variant="labelMedium" color="text.secondary">
          {t('LoggedOutGenerator.ScanDestination.description')}
        </Typography>
      </Box>
    </>
  )
}

export const ScanDestinationFieldsOptions: FC<{ isSmallVariant?: boolean }> = ({
  isSmallVariant
}) => {
  const scanDestinationOptions = useScanDestinationOptions()
  return (
    <Stack
      component="ul"
      gap={isSmallVariant ? { md: 2, xl: 4 } : 4}
      sx={{
        listStyleType: 'none',
        p: 0,
        mb: 0,
        display: 'grid',
        gridTemplateColumns: { md: 'repeat(3, 1fr)', lg: 'repeat(4, 1fr)' }
      }}
    >
      {Object.values(scanDestinationOptions).map(({ label, value, Icon }) => (
        <li key={value}>
          <ScanDestinationRadioCard
            label={label}
            value={value}
            Icon={Icon}
            isSmallVariant={isSmallVariant}
          />
        </li>
      ))}
    </Stack>
  )
}
